import { Controller, UseFormReturn, useWatch } from "react-hook-form";
import { EditContractInput } from "../../validation/editContract";
import { Grid, Group } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Contract, CreateOrderActiveValues, GetTeamMember } from "@/types";
import { useGetCompanyTeam } from "@/services/Company";
import Text from "@/components/atoms/Text";
import TextInput from "@/components/atoms/TextInput";
import RadioButtonGroup from "@/components/molecules/RadioButtonGroup";
import ContractEndTypeEnum from "@/constants/enums/ContractEndTypeEnum";
import Select from "@/components/atoms/Select";
import { useAuth } from "@/contexts/AuthContext";
import UserMembershipTypeEnum from "@/constants/enums/UserMembershipTypeEnum";
import DatePickerInput from "@/components/atoms/DatePickerInput";

interface SettingsProps {
  contract: Contract;
  disableForm: boolean;
  editContractForm: UseFormReturn<EditContractInput>;
  createOrderValues: CreateOrderActiveValues | undefined;
}

const Settings = ({ editContractForm, createOrderValues, disableForm }: SettingsProps) => {
  const { t } = useTranslation();

  const { currentCompany } = useAuth();

  const { data: teamMembers } = useGetCompanyTeam(currentCompany?.companyId as number, { staleTime: 1000 * 60 * 5 });

  const formContractType = useWatch({
    control: editContractForm.control,
    name: "contractType",
  });

  const contractTradeByOptions = [
    { value: ContractEndTypeEnum.AMOUNT, label: t("common:amount") },
    { value: ContractEndTypeEnum.DATEPERIOD, label: t("common:date") },
  ];

  const automaticMatchOptions = [
    { value: "YES", label: t("common:yes") },
    { value: "NO", label: t("common:no") },
  ];

  return (
    <>
      <Grid>
        <Grid.Col span={{ base: 12, xl: 6 }}>
          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:contractName")}
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Controller
                control={editContractForm.control}
                name="name"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    id="edit-contract-id"
                    disabled={disableForm}
                    onChange={onChange}
                    value={value}
                    error={error ? t("validation:" + error?.message) : undefined}
                  />
                )}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:startDate")}
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Controller
                control={editContractForm.control}
                name="contractStartDate"
                render={({ field: { onChange, value } }) => <DatePickerInput value={value} onChange={onChange} />}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:tradeBy")}
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Controller
                control={editContractForm.control}
                name="contractType"
                render={({ field: { onChange, value } }) => (
                  <RadioButtonGroup
                    id={"edit-contract-trade-by"}
                    name={"contractType"}
                    disabled={disableForm}
                    options={contractTradeByOptions}
                    justify="right"
                    defaultValue={value}
                    onValueChange={onChange}
                  />
                )}
              />
            </Grid.Col>

            {formContractType === ContractEndTypeEnum.AMOUNT && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" color="secondary">
                    {t("contracts:totalAmount")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Group gap="0" align="top" justify="right">
                    <Controller
                      control={editContractForm.control}
                      name="amount"
                      render={({ field: { onChange, value }, fieldState }) => (
                        <TextInput
                          id="edit-contract-amount"
                          disabled={disableForm}
                          flex={"0 0 70%"}
                          type="number"
                          onChange={onChange}
                          value={value ?? ""}
                          error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                        />
                      )}
                    />
                    <Controller
                      control={editContractForm.control}
                      name="amountUnitId"
                      render={({ field: { onChange, value }, fieldState }) => (
                        <Select
                          id="edit-contract-amount-unit"
                          disabled={disableForm}
                          flex={"0 0 30%"}
                          data={
                            createOrderValues?.units.map((unit) => ({
                              value: unit.id.toString(),
                              label: t("enumerables:weightUnits." + unit.value.toUpperCase()),
                            })) || []
                          }
                          value={value?.toString() || null}
                          onChange={onChange}
                          error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                        />
                      )}
                    />
                  </Group>
                </Grid.Col>
              </>
            )}
            {formContractType === ContractEndTypeEnum.DATEPERIOD && (
              <>
                <Grid.Col span={4}>
                  <Text size="sm" color="secondary">
                    {t("contracts:endDate")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={8}>
                  <Controller
                    control={editContractForm.control}
                    name="contractEndDate"
                    render={({ field: { onChange, value }, fieldState }) => (
                      <DatePickerInput
                        value={value}
                        onChange={onChange}
                        error={fieldState.error?.message ? t(fieldState.error?.message) : null}
                      />
                    )}
                  />
                </Grid.Col>
              </>
            )}
          </Grid>
        </Grid.Col>

        <Grid.Col span={{ base: 12, xl: 6 }}>
          <Grid>
            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:purchaseOrderNumber")}
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Controller
                control={editContractForm.control}
                name="poNumber"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    id="edit-contract-purchase-order-number"
                    disabled={disableForm}
                    onChange={onChange}
                    value={value ?? ""}
                    error={error ? t("validation:" + error?.message) : undefined}
                  />
                )}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:automaticSalesMatch")}
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Controller
                control={editContractForm.control}
                name="automaticSalesApproval"
                render={({ field: { onChange, value } }) => (
                  <RadioButtonGroup
                    id={"edit-contract-automatic-sales-approval"}
                    name={"automaticSalesApproval"}
                    disabled={disableForm}
                    options={automaticMatchOptions}
                    justify="right"
                    defaultValue={value ? "YES" : "NO"}
                    onValueChange={(e) => onChange(e === "YES" ? true : false)}
                  />
                )}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:contractResponsible")}
              </Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Controller
                control={editContractForm.control}
                name="contractManagerId"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Select
                    id="edit-contract-manager"
                    disabled={disableForm}
                    data={
                      teamMembers
                        ? teamMembers.activeUsers
                            .filter((member: GetTeamMember) => member.roleType !== UserMembershipTypeEnum.VIEWER)
                            .map((member: GetTeamMember) => {
                              return {
                                value: member.id.toString(),
                                label: `${member.firstName} ${member.lastName}`,
                              };
                            })
                        : []
                    }
                    onChange={onChange}
                    value={value?.toString() || null}
                    error={error?.message}
                  />
                )}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:contact")}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Controller
                control={editContractForm.control}
                name="contactEmail"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    id="edit-contact-email"
                    disabled={disableForm}
                    placeholder={t("common:email")}
                    onChange={onChange}
                    value={value || ""}
                    error={error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Controller
                control={editContractForm.control}
                name="contactPhone"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    id="edit-contact-phone"
                    disabled={disableForm}
                    placeholder={t("common:phone")}
                    onChange={onChange}
                    value={value || ""}
                    error={error?.message}
                  />
                )}
              />
            </Grid.Col>

            <Grid.Col span={4}>
              <Text size="sm" color="secondary">
                {t("contracts:contactCounterparty")}
              </Text>
            </Grid.Col>
            <Grid.Col span={4}>
              <Controller
                control={editContractForm.control}
                name="contactCounterpartyEmail"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    id="edit-contact-counterparty-email"
                    disabled={disableForm}
                    placeholder={t("common:email")}
                    onChange={onChange}
                    value={value || ""}
                    error={error?.message}
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <Controller
                control={editContractForm.control}
                name="contactCounterpartyPhone"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <TextInput
                    id="edit-contact-counterparty-phone"
                    disabled={disableForm}
                    placeholder={t("common:phone")}
                    onChange={onChange}
                    value={value || ""}
                    error={error?.message}
                  />
                )}
              />
            </Grid.Col>
          </Grid>
        </Grid.Col>
      </Grid>
    </>
  );
};

export default Settings;
